import { useState } from "react";
import { HeartPulse, X, Phone, AlertTriangle } from "lucide-react";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import type { PlayerHealthPublic } from "@dugads/shared";

// 159 -- match-day / tour quick access to a player's emergency contact + key
// medical info. Reuses the 145 GET /players/:id/health endpoint (no new data),
// which is role- + guardian-consent-gated (143) server-side: a 403 means the
// caller may not see the record (e.g. consent not granted), shown as a notice.
export function EmergencyContactButton({
  playerId,
  playerName,
}: {
  playerId: string;
  playerName: string;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [health, setHealth] = useState<PlayerHealthPublic | null>(null);
  const [denied, setDenied] = useState<string | null>(null);
  const [empty, setEmpty] = useState(false);

  async function load() {
    setOpen(true);
    setLoading(true);
    setDenied(null);
    setEmpty(false);
    setHealth(null);
    try {
      const res = await api.getPlayerHealth(playerId);
      if (res.health) setHealth(res.health);
      else setEmpty(true);
    } catch (e) {
      if (e instanceof api.ApiError && e.status === 403) {
        setDenied("You do not have consent-gated access to this player's medical record.");
      } else {
        setDenied(e instanceof api.ApiError ? e.message : "Failed to load emergency details");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={load}
        aria-label={`Emergency contact for ${playerName}`}
        title="Emergency contact + medical (match-day)"
        data-action="emergency-contact"
      >
        <HeartPulse className="h-4 w-4 text-[#791F1F]" />
      </Button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 px-4"
          onClick={() => setOpen(false)}
          data-region="emergency-dialog"
        >
          <div
            className="w-full max-w-sm rounded-2xl border border-border bg-background p-5 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-start justify-between">
              <div className="flex items-center gap-2">
                <HeartPulse className="h-5 w-5 text-[#791F1F]" aria-hidden />
                <h3 className="text-base font-semibold">{playerName}</h3>
              </div>
              <Button variant="ghost" size="sm" aria-label="Close" onClick={() => setOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            {loading && <p className="text-sm text-muted-foreground">Loading...</p>}

            {!loading && denied && (
              <div className="flex items-start gap-2 rounded-lg bg-[#FAEEDA] px-3 py-2 text-[#633806]">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <p className="text-xs">{denied}</p>
              </div>
            )}

            {!loading && empty && (
              <p className="text-sm text-muted-foreground">No medical record captured for this player yet.</p>
            )}

            {!loading && health && (
              <div className="flex flex-col gap-2 text-sm">
                <Field label="Emergency contact">
                  {health.emergency_contact_name || "--"}
                  {health.emergency_contact_phone && (
                    <a
                      href={`tel:${health.emergency_contact_phone}`}
                      className="ml-2 inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      <Phone className="h-3.5 w-3.5" /> {health.emergency_contact_phone}
                    </a>
                  )}
                </Field>
                <Field label="Allergies">{health.allergies || "None recorded"}</Field>
                <Field label="Medical conditions">{health.medical_conditions || "None recorded"}</Field>
                <Field label="Medical aid">
                  {health.has_medical_aid
                    ? `${health.medical_aid_name ?? "Yes"}${health.medical_aid_number ? ` (${health.medical_aid_number})` : ""}`
                    : "None"}
                </Field>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="mt-0.5">{children}</p>
    </div>
  );
}
